import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  DevHubBox,
  DevHubButton,
  DevHubGrid,
  DevHubTab,
  DevHubTabs,
} from '@devxdiscover/devhub-ui';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  isComponentType,
  isKind,
} from '@backstage/plugin-catalog';
import {
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
  EntityUserProfileCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import {
  SystemContext,
  SystemProvider,
} from '../../custom/contexts/SystemContext';
import {
  ApplicationDisplayProvider,
  Section,
  useApplicationDisplay,
} from '../../custom/contexts/ApplicationDisplayContext';
import { InsightsAbout } from '../../custom/components/insights/InsightsAbout';
import { CustomAttributes } from '../../custom/components/insights/customAttributes/CustomAttributes';

import {
  EntityConcourseSummary,
  isConcourseAvailable,
} from '@devxdiscover/plugin-concourse';
import {
  isCableEntity,
  isFeatureAvailable,
} from '../../custom/util/featureFlags';
import { ApplicationRoles } from '../../custom/components/insights/roles/ApplicationRoles';
import {
  EntityPluginPrivacyContent,
  PrivacyPluginInfo,
} from '@dxportal/plugin-privacy';
import { EntitySSOClient } from '@devxdiscover/plugin-iam';
import {
  PluginShowbackPage,
  TableauProvider,
} from '@comcastpepr/showback-devhub-plugin';
import { FeedbackContent } from '../../custom/components/insights/feedback/FeedbackContent';
import {
  ConfigFeatureCheck,
  ConfigIsCableEntity,
} from '../../custom/util/ConfigFeatureCheck';
import { PluginManager, PluginType } from '../plugin/PluginManager';
import ApplicationDevices from '../../custom/components/insights/devices/ApplicationDevices';
import { ApplicationCloudAccounts } from '../../custom/components/insights/cloudAccounts/ApplicationCloudAccounts';
import { ApplicationCloudFoundry } from '../../custom/components/insights/cloudFoundry/ApplicationCloudFoundry';
import { ApplicationAiResources } from '../../custom/components/insights/ai/ApplicationAiTab';
import { andConditions, canUserUpdateApp } from '../../custom/util/entityUtils';
import { AppHistory } from '../../custom/components/insights/AppHistory';
import { DefaultRenderLabel } from '../../custom/components/catalog/DependencyDiagram';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';
import GreenScoreCard from '../GreenScoreCard/GreenScoreCard';
import { SecurityWidgets } from './SecurityWidgets';
import { ApplicationDocs } from '../../custom/components/insights/ApplicationDocs';
import { ComponentsContent } from '../../custom/components/insights/components/ComponentsContent';
import ResourcesReportContent from '../../custom/components/insights/ResourceReportContent';
import { EntityDbaasContent } from '@devxdiscover/plugin-database';
import { SolutionsContent } from '../../custom/components/catalog/SolutionsContent';
import { AppNovaSettings } from '../../custom/components/agent/AppNovaSettings';
import { EntityAboutCardWithRefresh } from '../../custom/components/catalog/EntityAboutCardWithRefresh';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const entityWarningContent = <></>;

const cicdContent = (
  <DevHubGrid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <DevHubGrid item xs={12}>
      <EntitySwitch>
        <EntitySwitch.Case if={isConcourseAvailable}>
          <EntityConcourseSummary />
        </EntitySwitch.Case>

        <EntitySwitch.Case>
          <EmptyState
            title="No CI/CD available for this entity"
            missing="info"
            description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
            action={
              <DevHubButton
                variant="contained"
                color="primary"
                href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
              >
                Read more
              </DevHubButton>
            }
          />
        </EntitySwitch.Case>
      </EntitySwitch>
    </DevHubGrid>
    <ConfigFeatureCheck feature={Feature.SONARQUBE}>
      <DevHubGrid item md={12}>
        <EntitySonarQubeCard variant="gridItem" />
      </DevHubGrid>
    </ConfigFeatureCheck>
  </DevHubGrid>
);

const overviewContent = (
  <DevHubGrid container spacing={3} alignItems="stretch">
    {entityWarningContent}
    <DevHubGrid item md={6} xs={12}>
      <EntityAboutCardWithRefresh variant="gridItem" />
    </DevHubGrid>
    <DevHubGrid item md={6} xs={12}>
      <EntityCatalogGraphCard variant="gridItem" height={400} />
    </DevHubGrid>
    <DevHubGrid item md={12} xs={12}>
      <EntityLinksCard />
    </DevHubGrid>
  </DevHubGrid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <DevHubGrid container spacing={3} alignItems="stretch">
        <DevHubGrid item md={6}>
          <EntityProvidedApisCard />
        </DevHubGrid>
        <DevHubGrid item md={6}>
          <EntityConsumedApisCard />
        </DevHubGrid>
      </DevHubGrid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);
const appInsightsPage = (
  <SystemProvider>
    <ApplicationDisplayProvider>
      <AppInsightsPage />
    </ApplicationDisplayProvider>
  </SystemProvider>
);

function AppInsightsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { app } = useContext(SystemContext);
  const { config, canShowSection } = useApplicationDisplay();
  const [selectedComponentTab, setSelectedComponentTab] = useState(0);
  const [selectedResourceTab, setSelectedResourceTab] = useState(0);

  const uwlEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.UNIVERSAL_WORKLIST,
  );

  const isCloudAccountsEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.CLOUD_ACCOUNTS,
  );
  const isCloudFoundryEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.CLOUD_FOUNDRY,
  );
  const isAiInventoryEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.AI_INVENTORY,
  );
  const isResourceDatabaseEnabled = ConfigHelper.instance.isFeatureEnabled(
    Feature.RESOURCE_DATABASE_TAB,
  );
  const isAiSystem = () => app?.systemType === 'AiSystem';
  const renderAiSystemResources = () => {
    if (isAiInventoryEnabled) return <ApplicationAiResources />;
    return <></>;
  };

  useEffect(() => {
    if (location.pathname.endsWith('/components/apis')) {
      setSelectedComponentTab(1);
    } else {
      setSelectedComponentTab(0);
    }

    // Dynamically determine tab index based on enabled features and route order
    // Routes array is built as: [cloud-accounts (if enabled), cloud-foundry (if enabled), devices, database (if enabled), ai (if enabled)]
    let tabIndex = 0;
    if (location.pathname.endsWith('/resources/cloud-accounts')) {
      tabIndex = 0;
    } else if (location.pathname.endsWith('/resources/cloud-foundry')) {
      tabIndex = isCloudAccountsEnabled ? 1 : 0;
    } else if (location.pathname.endsWith('/resources/devices')) {
      tabIndex = 0;
      if (isCloudAccountsEnabled) tabIndex++;
      if (isCloudFoundryEnabled) tabIndex++;
    } else if (location.pathname.endsWith('/resources/database')) {
      tabIndex = 0;
      if (isCloudAccountsEnabled) tabIndex++;
      if (isCloudFoundryEnabled) tabIndex++;
      tabIndex++; // devices always present
    } else if (location.pathname.endsWith('/resources/ai')) {
      tabIndex = 0;
      if (isCloudAccountsEnabled) tabIndex++;
      if (isCloudFoundryEnabled) tabIndex++;
      tabIndex++; // devices always present
      if (isResourceDatabaseEnabled) tabIndex++;
    }
    setSelectedResourceTab(tabIndex);
  }, [
    location.pathname,
    isCloudAccountsEnabled,
    isCloudFoundryEnabled,
    isResourceDatabaseEnabled,
    isAiInventoryEnabled,
  ]);

  return (
    <EntityLayout>
      <EntityLayout.Route path="/" title="Insights">
        <DevHubGrid container alignItems="stretch">
          {entityWarningContent}
          <DevHubGrid item xs={12}>
            <InsightsAbout />
          </DevHubGrid>
          {!config.isExperimental && (
            <DevHubGrid container item tbMargin="medium">
              <ConfigFeatureCheck feature={Feature.RESOURCE_REPORT}>
                {canShowSection(Section.METRICS) && (
                  <DevHubGrid
                    item
                    xs={
                      ConfigHelper.instance.isFeatureEnabled(
                        Feature.GREEN_SCORE,
                      )
                        ? 6
                        : 12
                    }
                  >
                    <ResourcesReportContent />
                  </DevHubGrid>
                )}
              </ConfigFeatureCheck>
              <ConfigFeatureCheck feature={Feature.GREEN_SCORE}>
                {canShowSection(Section.GREEN_ENERGY) && (
                  <DevHubGrid
                    item
                    xs={
                      ConfigHelper.instance.isFeatureEnabled(
                        Feature.RESOURCE_REPORT,
                      )
                        ? 6
                        : 12
                    }
                  >
                    <GreenScoreCard
                      score={app?.greenEnergyProfile?.score || null}
                      metrics={app?.greenEnergyProfile?.metrics || []}
                    />
                  </DevHubGrid>
                )}
              </ConfigFeatureCheck>
            </DevHubGrid>
          )}
          <DevHubGrid item xs={12}>
            <AppHistory />
          </DevHubGrid>
          <ConfigIsCableEntity corporateEntity={app?.corporateEntity}>
            <DevHubGrid item xs={12}>
              <CustomAttributes />
            </DevHubGrid>
          </ConfigIsCableEntity>
        </DevHubGrid>
      </EntityLayout.Route>
      <EntityLayout.Route path="/components" title="Components">
        <>
          <DevHubTabs
            value={selectedComponentTab}
            onChange={(_event, newValue) => {
              setSelectedComponentTab(newValue);
              const routes = ['components', 'components/apis'];
              navigate(routes[newValue]);
            }}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            sx={{ mt: -2.4 }}
          >
            <DevHubTab label="Components" />
            <DevHubTab label="APIs" />
          </DevHubTabs>
          <DevHubBox mt={2}>
            <EntitySwitch>
              <EntitySwitch.Case
                if={() => location.pathname.endsWith('components')}
              >
                <ComponentsContent />
              </EntitySwitch.Case>
              <EntitySwitch.Case
                if={() => location.pathname.endsWith('components/apis')}
              >
                <DevHubGrid container>
                  <DevHubGrid item xs={12}>
                    <EntityHasApisCard variant="gridItem" />
                  </DevHubGrid>
                  <DevHubGrid item xs={12}>
                    <EntityProvidedApisCard />
                  </DevHubGrid>
                  <DevHubGrid item xs={12}>
                    <EntityConsumedApisCard />
                  </DevHubGrid>
                </DevHubGrid>
              </EntitySwitch.Case>
            </EntitySwitch>
          </DevHubBox>
        </>
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/dependency"
        title="Dependency"
        if={andConditions(
          isCableEntity(app?.corporateEntity),
          canUserUpdateApp(app, 'UpdateApplicationPolicy'),
          () => canShowSection(Section.DEPENDENCY),
          () => !isAiSystem(),
        )}
      >
        <>
          <DevHubTabs
            value={selectedResourceTab}
            onChange={(_event, newValue) => setSelectedResourceTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            sx={{
              mt: -3,
            }}
          >
            {isFeatureAvailable(Feature.CLOUD_ACCOUNTS) && (
              <DevHubTab label="Dependency Diagram" />
            )}

            <DevHubTab label="System Diagram" />
          </DevHubTabs>
          <DevHubBox mt={2}>
            {selectedResourceTab === 0 && (
              <EntityCatalogGraphCard
                variant="gridItem"
                title="Dependency Diagram"
                height={800}
                showArrowHeads
                relations={[RELATION_DEPENDENCY_OF, RELATION_DEPENDS_ON]}
                renderLabel={element => (
                  <DefaultRenderLabel edge={element.edge} />
                )}
                unidirectional
              />
            )}
            {selectedResourceTab === 1 && (
              <EntityCatalogGraphCard
                variant="gridItem"
                direction={Direction.TOP_BOTTOM}
                title="System Diagram"
                height={800}
                relations={[
                  RELATION_PART_OF,
                  RELATION_HAS_PART,
                  RELATION_API_CONSUMED_BY,
                  RELATION_API_PROVIDED_BY,
                  RELATION_CONSUMES_API,
                  RELATION_PROVIDES_API,
                ]}
                unidirectional={false}
              />
            )}
          </DevHubBox>
        </>
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/docs"
        title="Docs"
        if={isCableEntity(app?.corporateEntity)}
      >
        <ApplicationDocs />
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/feedback"
        title="Feedback"
        if={andConditions(
          isFeatureAvailable(Feature.FEEDBACK),
          isCableEntity(app?.corporateEntity),
          canUserUpdateApp(app, 'UpdateApplicationPolicy'),
        )}
      >
        <FeedbackContent />
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/iam"
        title="IAM"
        if={andConditions(
          isFeatureAvailable(Feature.IAM),
          isCableEntity(app?.corporateEntity),
          () => canShowSection(Section.IAM),
          () => !isAiSystem(),
        )}
      >
        <EntitySSOClient />
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/nova"
        title="Nova"
        if={isFeatureAvailable(Feature.NOVA)}
      >
        <AppNovaSettings />
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/privacy"
        title="Privacy"
        if={andConditions(
          isFeatureAvailable(Feature.PRIVACY_PLUGIN),
          isCableEntity(app?.corporateEntity),
          () => canShowSection(Section.PRIVACY),
          () => !isAiSystem(),
        )}
      >
        <PluginManager type={PluginType.TAB} modalContent={PrivacyPluginInfo}>
          <EntityPluginPrivacyContent />
        </PluginManager>
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/resources"
        title="Resources"
        if={isCableEntity(app?.corporateEntity)}
      >
        {isAiSystem() ? (
          // AiSystem: no sub-tabs — Resources tab IS the AI view
          renderAiSystemResources()
        ) : (
          // App: existing sub-tabs + new AI sub-tab at the end
          <>
            <DevHubTabs
              value={selectedResourceTab}
              onChange={(_event, newValue) => {
                setSelectedResourceTab(newValue);
                const routes: string[] = [];
                if (isCloudAccountsEnabled)
                  routes.push('resources/cloud-accounts');
                if (isCloudFoundryEnabled)
                  routes.push('resources/cloud-foundry');
                routes.push('resources/devices');
                if (isResourceDatabaseEnabled)
                  routes.push('resources/database');
                if (isAiInventoryEnabled) routes.push('resources/ai');
                navigate(routes[newValue]);
              }}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              sx={{
                mt: -3,
              }}
            >
              {isFeatureAvailable(Feature.CLOUD_ACCOUNTS) && (
                <DevHubTab label="Cloud Accounts" />
              )}

              {isFeatureAvailable(Feature.CLOUD_FOUNDRY) && (
                <DevHubTab label="Cloud Foundry" />
              )}

              <DevHubTab label="Devices" />
              {isResourceDatabaseEnabled && <DevHubTab label="Databases" />}
              {isAiInventoryEnabled && <DevHubTab label="AI" />}
            </DevHubTabs>
            <DevHubBox mt={2}>
              <EntitySwitch>
                {[
                  {
                    path: 'resources/cloud-accounts',
                    component: <ApplicationCloudAccounts />,
                  },
                  {
                    path: 'resources/cloud-foundry',
                    component: <ApplicationCloudFoundry />,
                  },
                  {
                    path: 'resources/devices',
                    component: <ApplicationDevices />,
                  },
                  ...(isResourceDatabaseEnabled
                    ? [
                        {
                          path: 'resources/database',
                          component: <EntityDbaasContent />,
                        },
                      ]
                    : []),
                  {
                    path: 'resources/ai',
                    component: <ApplicationAiResources />,
                  },
                  {
                    path: 'resources',
                    component: <ApplicationCloudAccounts />,
                  },
                ].map(({ path, component }) => (
                  <EntitySwitch.Case
                    key={path}
                    if={() => location.pathname.endsWith(path)}
                  >
                    {component}
                  </EntitySwitch.Case>
                ))}
              </EntitySwitch>
            </DevHubBox>
          </>
        )}
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/application-roles"
        title="Roles"
        if={andConditions(
          isFeatureAvailable(Feature.USER_ROLES),
          isCableEntity(app?.corporateEntity),
        )}
      >
        <ApplicationRoles />
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/security"
        title="Security"
        if={andConditions(
          isFeatureAvailable(Feature.DEVXPROTECT_EXPERIENCE),
          isCableEntity(app?.corporateEntity),
          () => canShowSection(Section.SECURITY),
        )}
      >
        <SecurityWidgets uwlEnabled={uwlEnabled} />
      </EntityLayout.Route>
      <EntityLayout.Route
        path="/showback"
        title="Showback"
        if={andConditions(
          isFeatureAvailable(Feature.SHOWBACK),
          isCableEntity(app?.corporateEntity),
          canUserUpdateApp(app, 'ShowbackTabPolicy'),
          () => canShowSection(Section.SHOWBACK),
        )}
      >
        <TableauProvider>
          <PluginShowbackPage />
        </TableauProvider>
      </EntityLayout.Route>

      <EntityLayout.Route
        path="/solutions"
        title="Q&A"
        if={isFeatureAvailable(Feature.SOLUTIONS)}
      >
        <SolutionsContent />
      </EntityLayout.Route>
    </EntityLayout>
  );
}

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI DevHubGrid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD" if={isConcourseAvailable}>
      {cicdContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <DevHubGrid container spacing={3}>
        {entityWarningContent}
        <DevHubGrid item md={6}>
          <EntityAboutCardWithRefresh variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item md={6}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </DevHubGrid>
        <DevHubGrid item md={6}>
          <EntityProvidingComponentsCard variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item md={6}>
          <EntityConsumingComponentsCard variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item md={6}>
          <EntityLinksCard variant="gridItem" />
        </DevHubGrid>
      </DevHubGrid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <DevHubGrid container spacing={3}>
        <DevHubGrid item xs={12}>
          <EntityApiDefinitionCard />
        </DevHubGrid>
      </DevHubGrid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <DevHubGrid container spacing={3}>
        {entityWarningContent}
        <DevHubGrid item xs={12} md={6}>
          <EntityUserProfileCard variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </DevHubGrid>
      </DevHubGrid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <DevHubGrid container spacing={3}>
        {entityWarningContent}
        <DevHubGrid item xs={12} md={6}>
          <EntityGroupProfileCard variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item xs={12} md={6}>
          <EntityOwnershipCard variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item xs={12}>
          <EntityMembersListCard />
        </DevHubGrid>
      </DevHubGrid>
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <DevHubGrid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <DevHubGrid item md={6}>
          <EntityAboutCardWithRefresh variant="gridItem" />
        </DevHubGrid>
        <DevHubGrid item md={6} xs={12}>
          <EntityCatalogGraphCard variant="gridItem" height={400} />
        </DevHubGrid>
        <DevHubGrid item md={6}>
          <EntityHasSystemsCard variant="gridItem" />
        </DevHubGrid>
      </DevHubGrid>
    </EntityLayout.Route>
  </EntityLayout>
);

/**
 * The entity page
 */
export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={appInsightsPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
